import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useI18n } from "@expertloop/i18n/react";
import { translationsApi, type TranslationFilter } from "../features/translations/api";
import {
	expectedPlaceholders,
	useTranslationKeys,
	validateOverride,
} from "../features/translations/useTranslationKeys";
import type { TranslationKeyEntry } from "../features/translations/types";
import "./TranslationsPage.css";

const FILTERS: TranslationFilter[] = ["all", "missing", "rejected", "override", "stale"];

export function TranslationDetailPage() {
	const { t } = useI18n();
	const { locale = "" } = useParams();
	const [searchParams] = useSearchParams();
	const {
		data,
		filter,
		search,
		page,
		isLoading,
		error,
		changeFilter,
		changeSearch,
		setPage,
		reload,
	} = useTranslationKeys(locale);

	// Le lien « clés à traiter » de la liste arrive avec un filtre en query.
	const [initialFilterApplied, setInitialFilterApplied] = useState(false);
	if (!initialFilterApplied) {
		const requested = searchParams.get("filter") as TranslationFilter | null;
		setInitialFilterApplied(true);
		if (requested && FILTERS.includes(requested)) changeFilter(requested);
	}

	const filterLabel = (value: TranslationFilter): string => {
		if (value === "all") return t("adminI18n.filterAll");
		if (value === "missing") return t("adminI18n.missing");
		if (value === "rejected") return t("adminI18n.rejected");
		if (value === "override") return t("adminI18n.override");
		return t("adminI18n.stale");
	};

	return (
		<div className="translations-page">
			<header className="content-header">
				<Link to="/translations" className="back-link">
					{t("adminI18n.backToLocales")}
				</Link>
				<h1>
					{t("adminI18n.title")} — {locale}
				</h1>
				{data && <p className="subtitle">{t("adminI18n.keysCount", { count: data.total })}</p>}
			</header>

			<div className="keys-toolbar">
				<input
					type="search"
					value={search}
					onChange={(event) => changeSearch(event.target.value)}
					placeholder={t("adminI18n.searchPlaceholder")}
					aria-label={t("adminI18n.searchPlaceholder")}
				/>
				<div className="filter-tabs" role="tablist">
					{FILTERS.map((value) => (
						<button
							key={value}
							type="button"
							role="tab"
							aria-selected={filter === value}
							className={`filter-tab ${filter === value ? "active" : ""}`}
							onClick={() => changeFilter(value)}
						>
							{filterLabel(value)}
						</button>
					))}
				</div>
			</div>

			{error && <div className="error-message">{t("adminI18n.loadFailed")}</div>}
			{isLoading && <div className="loading">{t("adminI18n.loading")}</div>}

			{!isLoading && data?.keys.length === 0 && (
				<p className="empty-state">{t("adminI18n.noKeys")}</p>
			)}

			<div className="keys-list">
				{data?.keys.map((entry) => (
					<KeyRow key={entry.key} locale={locale} entry={entry} onChanged={reload} />
				))}
			</div>

			{data && data.totalPages > 1 && (
				<div className="pagination">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={() => setPage(page - 1)}
						disabled={page <= 1}
					>
						{t("common.previous")}
					</button>
					<span>
						{t("adminI18n.pageOf", { page, totalPages: data.totalPages })}
					</span>
					<button
						type="button"
						className="btn btn-secondary"
						onClick={() => setPage(page + 1)}
						disabled={page >= data.totalPages}
					>
						{t("common.next")}
					</button>
				</div>
			)}
		</div>
	);
}

function KeyRow({
	locale,
	entry,
	onChanged,
}: {
	locale: string;
	entry: TranslationKeyEntry;
	onChanged: () => void;
}) {
	const { t } = useI18n();
	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState(entry.value ?? "");
	const [validationError, setValidationError] = useState<string | null>(null);
	const [actionError, setActionError] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	const placeholders = expectedPlaceholders(entry.key);

	const handleSave = async () => {
		const problem = validateOverride(entry.key, draft);
		if (problem) {
			setValidationError(
				placeholders.length > 0
					? t("adminI18n.placeholderMismatch", { expected: placeholders.join(", ") })
					: problem,
			);
			return;
		}

		setIsSaving(true);
		setActionError("");
		try {
			await translationsApi.saveKey(locale, entry.key, draft);
			setIsEditing(false);
			setValidationError(null);
			onChanged();
		} catch {
			setActionError(t("adminI18n.saveFailed"));
		} finally {
			setIsSaving(false);
		}
	};

	const handleReset = async () => {
		setActionError("");
		try {
			await translationsApi.resetKey(locale, entry.key);
			onChanged();
		} catch {
			setActionError(t("adminI18n.resetFailed"));
		}
	};

	const handleRetranslate = async () => {
		setActionError("");
		try {
			await translationsApi.retranslate(locale, { keys: [entry.key] });
		} catch {
			setActionError(t("adminI18n.retranslateFailed"));
		}
	};

	return (
		<article className={`key-row state-${entry.state}`}>
			<div className="key-row-head">
				<code className="key-name">{entry.key}</code>
				<span className={`state-badge ${entry.state}`}>{stateLabel(entry.state, t)}</span>
			</div>

			<div className="key-values">
				<div className="key-value">
					<span className="key-value-label">{t("adminI18n.sourceText")}</span>
					<p className="source-text">{entry.sourceValue}</p>
				</div>
				<div className="key-value">
					<span className="key-value-label">{t("adminI18n.translation")}</span>
					{isEditing ? (
						<>
							<textarea
								value={draft}
								onChange={(event) => {
									setDraft(event.target.value);
									setValidationError(null);
								}}
								rows={2}
								aria-label={t("adminI18n.translation")}
							/>
							{validationError && <p className="field-error">{validationError}</p>}
						</>
					) : (
						<p className={entry.value ? "translated-text" : "untranslated-text"}>
							{entry.value ?? t("adminI18n.untranslated")}
						</p>
					)}
				</div>
			</div>

			{entry.rejection && (
				<p className="key-hint rejection">
					{t("adminI18n.rejectionHint", { reason: entry.rejection.reason })}
				</p>
			)}
			{entry.state === "stale" && <p className="key-hint stale">{t("adminI18n.staleHint")}</p>}
			{entry.override && entry.state === "override" && (
				<p className="key-hint override">
					{t("adminI18n.correctedBy", { author: entry.override.by })}
				</p>
			)}
			{actionError && <p className="field-error">{actionError}</p>}

			<div className="key-actions">
				{isEditing ? (
					<>
						<button
							type="button"
							className="btn btn-primary"
							onClick={handleSave}
							disabled={isSaving || draft.trim().length === 0}
						>
							{t("adminI18n.saveKey")}
						</button>
						<button
							type="button"
							className="btn btn-secondary"
							onClick={() => {
								setIsEditing(false);
								setDraft(entry.value ?? "");
								setValidationError(null);
							}}
						>
							{t("adminI18n.cancelEdit")}
						</button>
					</>
				) : (
					<>
						<button
							type="button"
							className="btn btn-secondary"
							onClick={() => setIsEditing(true)}
						>
							{t("adminI18n.editKey")}
						</button>
						<button type="button" className="btn btn-secondary" onClick={handleRetranslate}>
							{t("adminI18n.retranslateKey")}
						</button>
						{entry.override && (
							<button type="button" className="btn btn-secondary" onClick={handleReset}>
								{t("adminI18n.resetKey")}
							</button>
						)}
					</>
				)}
			</div>
		</article>
	);
}

function stateLabel(
	state: TranslationKeyEntry["state"],
	t: (key: "adminI18n.translated" | "adminI18n.missing" | "adminI18n.rejected" | "adminI18n.override" | "adminI18n.stale") => string,
): string {
	if (state === "translated") return t("adminI18n.translated");
	if (state === "missing") return t("adminI18n.missing");
	if (state === "rejected") return t("adminI18n.rejected");
	if (state === "override") return t("adminI18n.override");
	return t("adminI18n.stale");
}
